import React from "react";
import Card from "components/Card";
import CardHeader from "components/CardHeader";
import ModalAddCredit from "screens/ModalAddCredit";
import ModalAddCharge from "screens/ModalAddCharge";
import ModalDelayedCharge from "screens/ModalDelayedCharge";
import ModalDeleteCharge from "screens/ModalDeleteCharge";
import ModalRestoreCharge from "screens/ModalRestoreCharge";
import ModalEditCharge from "screens/ModalEditCharge";
import ModalDownloadReceipt from "screens/ModalDownloadReceipt";
import ModalCouponCode from "screens/ModalCouponCode";
import ModalRetry from "screens/ModalRetry";
import ModalVoid from "screens/ModalVoid";
import ModalRefund from "screens/ModalRefund";
import TransactionTooltip from "components/TransactionTooltip";
import {
  BillOfSaleContainer,
  BillItemContainer,
  ItemContainer,
  Warning,
} from "./styles";
import moment from "moment-timezone";
import { TIME_ZONE } from "constants/constant";
import {
  EditIcon,
  PDFDownloadIcon,
  RemoveIcon,
  UndoRemoveIcon,
} from "assets/icons";
import { MAT_RATE, HST_RATE } from "constants/constant";

const BillOfSale = ({
  bill,
  setBill,
  bookings,
  primaryCard,
  groupId,
  groupStatus,
}) => {
  const billArray = [];

  const transactionTypeMap = {
    "01": "Authorization Only (01)",
    "02": "Sale Transaction (02)",
    "07": "Prior-Authorized Sale (07)",
    "09": "Return (09)",
    11: "Void Sale (11)",
    13: "Batch Close (13)",
    14: "Batch Create and Inquiry (14)",
    17: "Void Return (17)",
    22: "Transaction Inquiry (22)",
    37: "Token Request (37)",
    61: "Full Authorization Reversal (61)",
    73: "Heartbeat (73)",
    75: "Incremental Authorization (75)",
    76: "Partial Authorization Reversal (76)",
    80: "Cancel Message (80)",
  };

  const sortedCharges = bill.charges? [...bill.charges].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).sort((a, b) => b.type.localeCompare(a.type)): [];

  sortedCharges.forEach((charge) => {
    billArray.push(
      <ItemContainer key={charge.chargeId}>
        <td className={"description normal"+ (charge.isDeleted ? " deleted" : "")}>
          {`${charge.description}
          ${(charge.chargeDate && charge.description !== "Late Checkout" && charge.description !== "Early Checkin" )? '(' + moment(charge.chargeDate).tz(TIME_ZONE).format("MMM Do YYYY") + ')' : ''}
        `}
        </td>
        <td className={"room normal" + (charge.isDeleted ? " deleted" : "")}>{charge.roomNumber}</td>
        <td className={"price normal"+ (charge.isDeleted ? " deleted" : "")}>
          <span className="amount">
            {Number(charge.amount)> 0 ? "$" + Number(charge.amount).toFixed(2) : "-$" + (-Number(charge.amount)).toFixed(2)}
          </span>
        </td>
        <td className="action">
          {charge.isDeleted ? (
            <span className="edit disabled">
              <EditIcon />
            </span>
          ) : (
            <span className="edit">
              <ModalEditCharge
                buttonIcon={<EditIcon />}
                charge={charge}
                setBill={setBill}
              />
            </span>
          )}
          {charge.isDeleted ? (
            <span className="deleted">
              <ModalRestoreCharge
                buttonIcon={<UndoRemoveIcon />}
                charge={charge}
                setBill={setBill}
              />
            </span>
          ) : (
            <span className="delete">
              <ModalDeleteCharge
                buttonIcon={<RemoveIcon />}
                charge={charge}
                setBill={setBill}
              />
            </span>
          )}
        </td>
      </ItemContainer>
    );
  });

  const formatString = (input) => {
    if(typeof input !== "string") return "-"
    return !input
      ? "-"
      : input
          ?.split("_") // Split the string by underscores
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ) // Capitalize each word
          .join(" "); // Join the words with spaces
  };

  const transactionArray = [];

  const sortedTransactions = bill.transactions? [...bill.transactions].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) : [];
  sortedTransactions.forEach((transaction) => {
    if (
      // transaction is not voided
      transaction.elavonTransactionType !== "11" &&
      transaction.elavonTransactionType !== "17"
    ) {
      transactionArray.push(
        <ItemContainer
          key={transaction.transactionId}
          className={transaction.type?.toLowerCase()}
        >
          <td
            className={
              "transaction" +
              (transaction.status === "APPROVAL" ||
              transaction.status === "ACKNOWLEDGED"
                ? transaction.type === "REFUND"
                  ? " warning"
                  : " normal"
                : "")
            }
          >
            {transactionTypeMap[transaction.elavonTransactionType] +
              (transaction.isVoided ? " (Voided)" : "")}
            <TransactionTooltip
              warning={transaction.type === "REFUND"}
              isVoided={transaction.isVoided}
              createdAt={transaction.createdAt}
              status={formatString(transaction.status)}
              amount={"$" + Number(transaction.amount).toFixed(2)}
              originator={transaction.originator}
              cardNumber={transaction.creditCardNumber?.slice(-4)}
              description={transaction.description}
              voidedAt={transaction.updatedAt}
              voidOriginator={transaction.voidOriginator}
              voidDescription={transaction.voidDescription}
            />
          </td>
          <td
            className={
              "status " +
              (transaction.status === "ACKNOWLEDGED"
                ? "approval"
                : transaction.status?.toLowerCase())
            }
          >
            {transaction.status === "ACKNOWLEDGED" ||
            transaction.status === "APPROVAL"
              ? "A"
              : "D"}
          </td>
          <td
            className={
              "date" +
              (transaction.status === "APPROVAL" ||
              transaction.status === "ACKNOWLEDGED"
                ? " normal"
                : "")
            }
          >
            <div>
              {moment(transaction.isVoided? transaction.updatedAt
                : transaction.createdAt)
                .tz(TIME_ZONE)
                .format("MMM Do")}
            </div>
            <div>
              {moment(transaction.isVoided? transaction.updatedAt
                : transaction.createdAt)
                .tz(TIME_ZONE)
                .format("hh:mm A")}
            </div>
          </td>
          <td
            className={
              "price" +
              (transaction.status === "APPROVAL" ||
              transaction.status === "ACKNOWLEDGED"
                ? transaction.type === "REFUND"
                  ? " warning"
                  : " normal"
                : "")
            }
          >
            <span
              className={
                "amount" +
                ((transaction.status === "APPROVAL" ||
                  transaction.status === "ACKNOWLEDGED") &&
                transaction.isVoided === false
                  ? ""
                  : " lineThrough")
              }
            >
              {(transaction.type === "REFUND" ? "-$" : "$") +
                Number(transaction.amount).toFixed(2)}
            </span>
            <ModalDownloadReceipt
              buttonIcon={<PDFDownloadIcon />}
              transactionId={transaction.transactionId}
            />
          </td>
          <td className="action">
            {transaction.status !== "APPROVAL" &&
            transaction.status !== "ACKNOWLEDGED" &&
            !transaction.isRetried ? (
              <ModalRetry
                buttonStyle={`
                  width: 64px;
                `}
                transactionId={transaction.transactionId}
                cardNumber={transaction.creditCardNumber}
                setBill={setBill}
              />
            ) : (transaction.type === "SALE" ||
                transaction.type === "SETTLER") &&
              !transaction.isVoided &&
              !transaction.isRefunded &&
              moment(transaction.createdAt).tz(TIME_ZONE).add(7, "days") >
                moment().tz(TIME_ZONE) ? (
              <ModalRefund
                buttonStyle={`
                  width: 74px;
              `}
                transactionId={transaction.transactionId}
                cardNumber={transaction.creditCardNumber}
                max={transaction.amount}
                setBill={setBill}
              />
            ) : transaction.type === "REFUND" &&
              !transaction.isVoided &&
              moment(transaction.createdAt).tz(TIME_ZONE).add(7, "days") >
                moment().tz(TIME_ZONE) ? (
              <ModalVoid
                buttonStyle={`
                  width: 58px;
                `}
                amount={transaction.amount}
                voidType="REFUND"
                transactionId={transaction.transactionId}
                cardNumber={transaction.creditCardNumber}
                setBill={setBill}
              />
            ) : null}
          </td>
        </ItemContainer>
      );
    }
  });

  return (
    <Card
      additionalstyle={`
        @media only screen and (max-width: 767px) {

          .type {
            width: 100%;
            margin-bottom: 10px; 
          }

          .buttonContainer {
            width: 100%;
            justify-content: space-between;
          }
          
          flex-direction: column;
          justify-content: center;
        }          
      `}
    >
      <CardHeader>
        <div className="type">BILL OF SALE</div>
        <div className="buttonContainer">
          {groupStatus === "PROCESSED" ? (
            <ModalDelayedCharge
              buttonStyle={`
                width: 123px;
                margin-left: 16px;
              `}
              billId={bill.billId}
              cardNumber={primaryCard.transaction?.creditCardNumber}
            />
          ) : (
            <>
              <ModalCouponCode
                buttonStyle={`
                  width: 108px;
                `}
                groupId={groupId}
                setBill={setBill}
              />
              <ModalAddCredit
                buttonStyle={`
                  width: 94px;
                  margin-left: 16px;
                `}
                bill={bill}
                setBill={setBill}
                cardNumber={primaryCard.transaction?.creditCardNumber}
              />
              <ModalAddCharge
                buttonStyle={`
                  width: 100px;
                  margin-left: 16px;
                `}
                bill={bill}
                setBill={setBill}
                cardNumber={primaryCard.transaction?.creditCardNumber}
                balance={bill.balance}
                bookings={bookings}
              />
            </>
          )}
        </div>
      </CardHeader>
      <BillItemContainer>
        <div className="title">BOOKING BREAKDOWN</div>
        <table className="billTable">
          <thead>
            <ItemContainer left>
              <th className="description">TRANSACTION</th>
              <th className="room">ROOM</th>
              <th className="price">PRICE</th>
              <th className="action">ACTION</th>
            </ItemContainer>
          </thead>
          <tbody>{billArray}</tbody>
        </table>
      </BillItemContainer>
      <BillOfSaleContainer>
        <div className="MAT">
          <div className="title">{`Municipal Accommodation Tax (${
            MAT_RATE * 100
          }%)`}</div>
          <div className="price normal">
            <span className="amount">
              {"$" + (bill.MAT?.toFixed(2) || "0.00")}
            </span>
          </div>
        </div>
        <div className="HST">
          <div className="title">{`HST (${HST_RATE * 100}%)`}</div>
          <div className="price normal">
            <span className="amount">
              {"$" + (bill.HST?.toFixed(2) || "0.00")}
            </span>
          </div>
        </div>
        <div className="total">
          <div className="title">TOTAL</div>
          <div className="price normal">
            <span className="amount">
              {"$" + (bill.total?.toFixed(2) || "0.00")}
            </span>
          </div>
        </div>
        <div className="paid">
          <div className="title">ALREADY PAID</div>
          <div className="price warning">
            <span className="amount">
              {"-$" + (bill.alreadyPaid?.toFixed(2) || "0.00")}
            </span>
          </div>
        </div>
        <div className="balance">
          <div className="title">BALANCE OUTSTANDING</div>
          <div className="price normal">
            <span className="amount">
              {(Number(bill.balance)>=0? "$" + Number(bill.balance).toFixed(2): "-$" + (-Number(bill.balance)).toFixed(2)) || "0.00"}
            </span>
          </div>
        </div>
      </BillOfSaleContainer>
      <BillItemContainer>
        <div className="title">TRANSACTIONS</div>
        {transactionArray.length ? (
          <table className="transactionTable">
            <thead>
              <ItemContainer>
                <th className="transaction">TRANSACTION</th>
                <th className="status">STAT</th>
                <th className="date">DATE</th>
                <th className="price">PRICE</th>
                <th className="action">ACTION</th>
              </ItemContainer>
            </thead>
            <tbody>{transactionArray}</tbody>
          </table>
        ) : (
          <Warning>No transaction has been added.</Warning>
        )}
      </BillItemContainer>
    </Card>
  );
};

export default BillOfSale;
